import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, type AdminAccountCreateIn, type AdminUserCreateIn } from "@/lib/api";

// ============ Accounts ============

export function useAdminStats() {
  return useQuery({
    queryKey: ["admin", "stats"],
    queryFn: () => api.admin.stats(),
    staleTime: 60_000,
  });
}

export function useAdminAccounts(query?: { q?: string; status?: string }) {
  return useQuery({
    queryKey: ["admin", "accounts", query],
    queryFn: () => api.admin.listAccounts({ ...query, limit: 200 }),
    staleTime: 30_000,
  });
}

export function useAdminAccount(id: number | null) {
  return useQuery({
    queryKey: ["admin", "accounts", id],
    queryFn: () => api.admin.getAccount(id!),
    enabled: id != null,
  });
}

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminAccountCreateIn) => api.admin.createAccount(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "accounts"] });
      qc.invalidateQueries({ queryKey: ["admin", "stats"] });
      toast.success("Cuenta creada");
    },
  });
}

export function usePatchAccount(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof api.admin.patchAccount>[1]) => api.admin.patchAccount(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "accounts"] });
      toast.success("Cuenta actualizada");
    },
  });
}

export function useSuspendAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.admin.suspendAccount(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "accounts"] });
      toast.success("Cuenta suspendida");
    },
  });
}

export function useActivateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.admin.activateAccount(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "accounts"] });
      toast.success("Cuenta activada");
    },
  });
}

// ============ Users (platform-wide) ============

export function useAdminAllUsers(query?: { q?: string; account_id?: number }) {
  return useQuery({
    queryKey: ["admin", "users-all", query],
    queryFn: () => api.admin.listAllUsers({ ...query, limit: 200 }),
    staleTime: 30_000,
  });
}

export function useAdminAllProjects(query?: { q?: string; account_id?: number }) {
  return useQuery({
    queryKey: ["admin", "projects-all", query],
    queryFn: () => api.admin.listAllProjects({ ...query, limit: 200 }),
    staleTime: 30_000,
  });
}

export function useCreateSuperadmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string; name: string }) =>
      api.admin.createSuperadmin(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users-all"] });
      qc.invalidateQueries({ queryKey: ["admin", "stats"] });
      toast.success("Superadmin creado");
    },
  });
}

export function usePatchUserGlobal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, body }: { userId: number; body: Parameters<typeof api.admin.patchUser>[1] }) =>
      api.admin.patchUser(userId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users-all"] });
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success("Usuario actualizado");
    },
  });
}

export function useDeleteUserGlobal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => api.admin.deleteUser(userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users-all"] });
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success("Usuario eliminado");
    },
  });
}

// ============ Users (por account) ============

export function useAdminUsers(accountId: number | null) {
  return useQuery({
    queryKey: ["admin", "users", accountId],
    queryFn: () => api.admin.listUsers(accountId!),
    enabled: accountId != null,
  });
}

export function useCreateUser(accountId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminUserCreateIn) => api.admin.createUser(accountId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users", accountId] });
      qc.invalidateQueries({ queryKey: ["admin", "accounts", accountId] });
      toast.success("Usuario creado");
    },
  });
}

export function usePatchUser(accountId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, body }: { userId: number; body: Parameters<typeof api.admin.patchUser>[1] }) =>
      api.admin.patchUser(userId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users", accountId] });
      toast.success("Usuario actualizado");
    },
  });
}

export function useDeleteUser(accountId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => api.admin.deleteUser(userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users", accountId] });
      toast.success("Usuario eliminado");
    },
  });
}

// ============ API keys ============

export function useAdminApiKeys(accountId: number | null) {
  return useQuery({
    queryKey: ["admin", "api-keys", accountId],
    queryFn: () => api.admin.listApiKeys(accountId!),
    enabled: accountId != null,
  });
}

export function useCreateApiKey(accountId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; scopes?: string[] }) => api.admin.createApiKey(accountId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "api-keys", accountId] });
    },
  });
}

export function useRevokeApiKey(accountId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.admin.revokeApiKey(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "api-keys", accountId] });
      toast.success("API key revocada");
    },
  });
}

export function useDeleteApiKey(accountId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.admin.deleteApiKey(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "api-keys", accountId] });
      toast.success("API key eliminada");
    },
  });
}

export function useAdminPlans() {
  return useQuery({
    queryKey: ["admin", "plans"],
    queryFn: () => api.admin.listPlans(),
    staleTime: 5 * 60_000,
  });
}
